import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Container, Row, Col, Button, Carousel, Spinner, Alert, Modal } from 'react-bootstrap';
import { FaMapMarkerAlt, FaUser, FaShareAlt, FaHeart, FaArrowLeft, FaTags, FaComment, FaWeight, FaBirthdayCake, FaHashtag, FaPaw, FaWhatsapp, FaEnvelope, FaFacebook, FaTwitter, FaLink } from 'react-icons/fa';
import { toast } from 'react-toastify';
import './AdDetails.css';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../firebase';
import { ref, get, set } from '@firebase/database';
import { API_ENDPOINTS, getImageUrl } from '../config/api';

const AdDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [startingChat, setStartingChat] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    const fetchAdDetails = async () => {
      try {
        const response = await axios.get(API_ENDPOINTS.ADS.GET_BY_ID(id));
        setAd(response.data);
        
        // Check if the current user has favorited this ad
        if (currentUser && response.data.favorites) {
          setIsFavorite(response.data.favorites.includes(currentUser.uid));
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching ad details:', err);
        setError('Failed to load ad details');
        setLoading(false);
        toast.error('Could not load ad details');
      }
    };

    fetchAdDetails();
  }, [id, currentUser]);

  const handleStartChat = async () => {
    if (!currentUser) {
      toast.error('Please login to chat with the seller');
      navigate('/login');
      return;
    }

    if (!ad || !ad.userId) {
      toast.error('Cannot start chat: Missing seller information');
      return;
    }

    if (currentUser.uid === ad.userId) {
      toast.error('This is your own ad');
      return;
    }

    try {
      setStartingChat(true);

      // Check if chat already exists for this ad and seller
      const userChatsRef = ref(database, `user_chats/${currentUser.uid}`);
      const userChatsSnapshot = await get(userChatsRef);
      
      let existingChatId = null;
      
      if (userChatsSnapshot.exists()) {
        const userChats = userChatsSnapshot.val();
        
        // Check each chat to see if it matches this ad and seller
        for (const chatId of Object.keys(userChats)) {
          // Get the full chat details to verify adId and participants
          const chatRef = ref(database, `chats/${chatId}`);
          const chatSnapshot = await get(chatRef);
          
          if (chatSnapshot.exists()) {
            const chat = chatSnapshot.val();
            // Check if this chat is for the same ad and seller
            if (chat.adId === id && chat.participants?.[ad.userId]) {
              existingChatId = chatId;
              break;
            }
          }
        }
      }

      // If existing chat found, navigate to it
      if (existingChatId) {
        console.log('Existing chat found:', existingChatId);
        navigate(`/chat/${existingChatId}`);
        toast.info('Opening existing chat');
        return;
      }

      // Create a new chat if no existing chat found
      const chatId = `chat_${Date.now()}`;
      const chatData = {
        adId: id,
        adTitle: ad.title,
        adImage: ad.images?.[0] || '',
        participants: {
          [currentUser.uid]: {
            id: currentUser.uid,
            name: currentUser.displayName || currentUser.email,
            unreadCount: 0
          },
          [ad.userId]: {
            id: ad.userId,
            name: ad.seller?.name || 'Seller',
            unreadCount: 0
          }
        },
        createdAt: Date.now(),
        lastMessage: 'Chat started',
        lastMessageTime: Date.now(),
        messages: {}
      };

      // Save the chat data
      const chatRef = ref(database, `chats/${chatId}`);
      await set(chatRef, chatData);
      
      // Create user_chats entries for both participants (separate writes to avoid root update stack overflow)
      const myChatEntry = {
        adId: id,
        adTitle: ad.title,
        otherUserName: ad.seller?.name || 'Seller',
        lastMessage: 'Chat started',
        timestamp: Date.now(),
        unreadCount: 0
      };
      const sellerChatEntry = {
        adId: id,
        adTitle: ad.title,
        otherUserName: currentUser.displayName || currentUser.email,
        lastMessage: 'Chat started',
        timestamp: Date.now(),
        unreadCount: 0
      };
      await set(ref(database, `user_chats/${currentUser.uid}/${chatId}`), myChatEntry);
      await set(ref(database, `user_chats/${ad.userId}/${chatId}`), sellerChatEntry);

      // Navigate to the chat
      navigate(`/chat/${chatId}`);
      toast.success('Chat started successfully');
    } catch (error) {
      console.error('Error starting chat:', error);
      toast.error('Failed to start chat. Please try again.');
    } finally {
      setStartingChat(false);
    }
  };

  const toggleFavorite = async () => {
    if (!currentUser) {
      toast.info('Please login to add to favorites');
      navigate('/login');
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      await axios.post(
        API_ENDPOINTS.ADS.FAVORITE(id), 
        {}, 
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          } 
        }
      );

      setIsFavorite(!isFavorite);
      toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
    } catch (err) {
      console.error('Favorite toggle error:', err);
      toast.error('Failed to update favorites');
    }
  };

  const shareAd = () => {
    setShowShareModal(true);
  };

  const handleShare = (platform) => {
    const shareUrl = window.location.href;
    const shareTitle = ad.title;
    const shareText = `Check out this ${ad.category} on FarmX: ${ad.title} - ${shareUrl}`;

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText)}`;
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
        break;
      default:
        break;
    }
    setShowShareModal(false);
  };

  const goBack = () => {
    navigate(-1);
  };

  if (loading) return (
    <Container className="text-center my-5">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </Container>
  );

  if (error) return (
    <Container className="text-center text-danger my-5">
      <Alert variant="danger">{error}</Alert>
    </Container>
  );

  if (!ad) return (
    <Container className="text-center my-5">
      <p>Ad not found</p>
    </Container>
  );

  return (
    <Container className="ad-details-container mt-4">
      <Button 
        variant="outline-secondary" 
        onClick={goBack} 
        className="mb-3"
      >
        <FaArrowLeft className="me-2" /> Back to Listings
      </Button>

      <Row>
        <Col md={7}>
          <Carousel>
            {ad.images && ad.images.length > 0 ? (
              ad.images.map((image, index) => (
                <Carousel.Item key={index}>
                  <img
                    className="d-block w-100"
                    src={getImageUrl(image)}
                    alt={`Slide ${index + 1}`}
                  />
                </Carousel.Item>
              ))
            ) : (
              <Carousel.Item>
                <img
                  className="d-block w-100"
                  src="https://via.placeholder.com/800x600?text=No+Image+Available"
                  alt="No advertisement images"
                />
              </Carousel.Item>
            )}
          </Carousel>
        </Col>

        <Col md={5}>
          <div className="ad-details-sidebar">
            <div className="ad-price-section">
              <h2 className="ad-price">₹{ad.price.toLocaleString()}</h2>
              <div className="ad-title">{ad.title}</div>
              <div className="ad-location">
                <FaMapMarkerAlt className="me-2" /> 
                {ad.neighbourhood && <span>{ad.neighbourhood}, </span>}
                {ad.city && <span>{ad.city}, </span>}
                {ad.state && <span>{ad.state}</span>}
              </div>
              <div className="ad-category mt-2">
                <FaTags className="me-2" />
                {ad.category || 'Category not specified'}
              </div>
              <div className="ad-date text-muted mt-2">
                Posted on: {new Date(ad.createdAt).toLocaleDateString()}
              </div>
            </div>

            <div className="seller-info mt-4">
              <h4>Seller Details</h4>
              <div className="d-flex align-items-center">
                <FaUser className="me-2" />
                <span>{ad.seller?.name || 'Anonymous Seller'}</span>
              </div>
            </div>

            <div className="ad-actions mt-4">
              <Button 
                variant="success" 
                className="w-100 mb-2"
                onClick={handleStartChat}
                disabled={startingChat}
              >
                {startingChat ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Starting Chat...
                  </>
                ) : (
                  <>
                    <FaComment className="me-2" /> Chat with Seller
                  </>
                )}
              </Button>

              <div className="d-flex justify-content-between">
                <Button 
                  variant={isFavorite ? "danger" : "outline-danger"} 
                  className="me-2 flex-grow-1"
                  onClick={toggleFavorite}
                >
                  <FaHeart className="me-2" />
                  {isFavorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </Button>
                
                <Button 
                  variant="outline-secondary" 
                  className="flex-grow-1"
                  onClick={shareAd}
                >
                  <FaShareAlt className="me-2" /> Share
                </Button>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mt-4">
        <Col>
          <div className="specifications-section">
            <h3>Specifications</h3>
            <Row>
              {ad.type && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaPaw className="me-2" />
                    <span className="spec-label">Type:</span>
                    <span className="spec-value">{ad.type}</span>
                  </div>
                </Col>
              )}
              {ad.breed && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaPaw className="me-2" />
                    <span className="spec-label">Breed:</span>
                    <span className="spec-value">{ad.breed}</span>
                  </div>
                </Col>
              )}
              {ad.age && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaBirthdayCake className="me-2" />
                    <span className="spec-label">Age:</span>
                    <span className="spec-value">{ad.age}</span>
                  </div>
                </Col>
              )}
              {ad.weight && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaWeight className="me-2" />
                    <span className="spec-label">Weight:</span>
                    <span className="spec-value">{ad.weight} kg</span>
                  </div>
                </Col>
              )}
              {ad.quantity && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaHashtag className="me-2" />
                    <span className="spec-label">Quantity:</span>
                    <span className="spec-value">{ad.quantity}</span>
                  </div>
                </Col>
              )}
              {ad.gender && (
                <Col md={6} className="mb-3">
                  <div className="spec-item">
                    <FaUser className="me-2" />
                    <span className="spec-label">Gender:</span>
                    <span className="spec-value">{ad.gender}</span>
                  </div>
                </Col>
              )}
            </Row>
          </div>

          <div className="description-section mt-4">
            <h3>Description</h3>
            <p>{ad.description || 'No description provided'}</p>
          </div>
        </Col>
      </Row>

      {/* Share Modal */}
      <Modal 
        show={showShareModal} 
        onHide={() => setShowShareModal(false)} 
        centered
        className="share-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Share this ad</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-grid gap-2">
            <Button 
              variant="success" 
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleShare('whatsapp')}
            >
              <FaWhatsapp /> Share on WhatsApp
            </Button>
            <Button 
              variant="primary" 
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleShare('email')}
            >
              <FaEnvelope /> Share via Email
            </Button>
            <Button 
              variant="info" 
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleShare('facebook')}
            >
              <FaFacebook /> Share on Facebook
            </Button>
            <Button 
              variant="info" 
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleShare('twitter')}
            >
              <FaTwitter /> Share on Twitter
            </Button>
            <Button 
              variant="secondary" 
              className="d-flex align-items-center justify-content-center gap-2"
              onClick={() => handleShare('copy')}
            >
              <FaLink /> Copy Link
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default AdDetails;
